import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Follitropin beta — one molecule, one device, and a pen that is not prefilled.
 *
 * Every other pen in this catalogue arrives with the medicine already in it.
 * This one does not: the cartridge is the medicine, the pen is a separate
 * durable object she keeps, and the two are sold apart. Both labels agree —
 * the EMA product information lists cartridges of 300, 600 and 900 IU and
 * **contains no vial at all**, and the FDA lists the same three cartridges,
 * "to be used with the FOLLISTIM Pen (supplied separately)".
 *
 * That matters for the step order. She loads a cartridge only when the last one
 * ran out, so `prepare` says "if needed" rather than making it a ritual, and
 * the pen itself is never disposed of — only the needle and, at 28 days, the
 * cartridge.
 *
 * ### The two formats the content still describes, and why no walk offers them
 *
 * `howToTakeByForm` carries a `vial` and a `pre_filled_syringe` procedure, and
 * the generic `howToTake` is a syringe procedure too. Puregon is sold as none
 * of them, in either market. They stay in the content — that is Anaïs's call,
 * 2026-09-28 — but `prescriptionForms` names the pen alone, so a patient is
 * never offered a walk for a device she cannot be holding.
 *
 * ### What the label states, and what it does not
 *
 * The needle **is** named here, which is unusual: the pack ships BD Micro-Fine
 * 0.33 mm × 12.7 mm (29 G) needles and the instructions-for-use say so. No
 * follitropin **alfa** label states a gauge anywhere — see
 * `biosimilar-pen-protocol.test.ts`, which pins that absence. The asymmetry is
 * real and it is the label's, not ours.
 *
 * The angle and the hold **are** stated, and an earlier draft of this file said
 * they were not. The instructions-for-use: *"Use a quick 'dart-like' motion to
 * insert the needle straight up and down (90° angle)"* and *"Wait for 5 seconds
 * before pulling the needle out of the skin"*. Both are tier **L**. The draft
 * had also carried "29-30 gauge" and a ten-second hold; the label says *"Use
 * only the BD Micro-Fine 0.33 mm × 12.7 mm (29G) Pen Needles as supplied"*, so
 * the 30 was a needle its own label forbids.
 *
 * ### What the European leaflet does not carry
 *
 * The EMA product information names **no site, no angle and no hold time** —
 * it says only "injections just under the skin (in the lower stomach, for
 * example)" and defers to a pen instruction manual published separately from
 * it. So the three facts above are the **US** document's, served to a European
 * patient too. The device is the same and this is very probably harmless, but
 * it is the load-bearing gap in this walk's evidence and it is named here
 * rather than left implied. See `TECHNIQUE.md`.
 */

const BRANDS = [
  // One molecule under two names, split by market: Follistim in the US, Puregon
  // in the EEA and the rest. Neither name is sold in the other's territory, so
  // `coachNameFromPrescribedName` speaks whichever one her prescription carries.
  // Taken from the content's own `availability`, which is the list a reviewer
  // maintains. `"EU"` is the bloc — a brand list uses it for "the name on the
  // box across the EEA" — so naming FR beside it, as an earlier draft did, both
  // duplicated it and broke the convention `markets.ts` documents.
  {
    name: "Puregon",
    markets: ["EU", "GB", "CH", "CA", "AU", "NZ", "SG", "TH", "ID", "MY"],
  },
  { name: "Follistim", markets: ["US", "JP"] },
  { name: "Follistim AQ", markets: ["US", "JP"] },
] as const satisfies readonly ProtocolBrand[];

/**
 * The same sentence follitropin alfa carries, and for the same reason.
 *
 * Puregon's content said "if less than 2 hours late, inject right away; if more
 * than 2 hours late, contact your clinic" in four places. **No gonadotropin
 * label states an hour** — Puregon's own reads, in full, "If you forget a dose
 * do not use a double dose to make up for a missed dose. Contact your doctor."
 * The old rule pointed the wrong way too: a patient three hours late on a
 * Sunday read a threshold she had already crossed and skipped a stimulation
 * day. See issue #3653 for the classes still carrying it.
 *
 * The third sentence is tier **P** and awaits Metrodoras.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "puregon.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
    fr: "Dose oubliée ou en retard - Ne doublez jamais la dose pour rattraper. Prévenez votre centre dès que possible et suivez ses consignes. Si vous ne pouvez pas les joindre le jour même, faites l'injection quand vous y pensez plutôt que de sauter la dose, et signalez-le à votre prochain rendez-vous.",
  },
  sourceSegment:
    "⚠ Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
};

/** OHSS, in the words this molecule's content uses everywhere. */
const WHEN_TO_CALL: ProtocolWarning = {
  id: "puregon.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
    fr: "Quand contacter votre centre - Arrêtez d'injecter et appelez votre centre en cas de prise de poids rapide de plus de 1 kg/jour, douleurs abdominales sévères, nausées ou vomissements, difficultés respiratoires, diminution de la diurèse ou signes d'infection au point d'injection.",
  },
  sourceSegment:
    "⚠ When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
};

/**
 * The half of "when to call" that does not belong to a clinic.
 *
 * The content listed a spreading site reaction, an allergic reaction and
 * breathlessness in one sentence with one destination. Anaphylaxis is not a
 * phone call to a fertility clinic (Anaïs, 2026-09-22), so the content was
 * split before this restated it. Breathlessness appears in both on purpose:
 * respiratory OHSS is days of ascites and goes to the clinician, sudden
 * breathlessness with swelling is minutes and goes to emergency services.
 */
const BREATHING_OR_ALLERGIC_REACTION: ProtocolWarning = {
  id: "puregon.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires soudaines, un gonflement du visage ou de la gorge, une éruption cutanée ou de l'urticaire nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
};

const STEPS: ProtocolStep[] = [
  {
    // "If needed", which is the whole difference from a prefilled pen: most
    // evenings there is nothing to load, and a step that insisted otherwise
    // would have her opening a cartridge she has most of.
    id: "puregon.pen.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash hands and verify the cartridge expiry date. Insert a new cartridge if needed and attach a fresh 29 gauge, half-inch pen needle - use only the needles supplied with your cartridge, removing both inner and outer caps.",
      fr: "Préparer - Lavez-vous les mains et vérifiez la péremption de la cartouche. Insérez une nouvelle cartouche si nécessaire et fixez une aiguille pour stylo 29 G, 12,7 mm neuve - n'utilisez que les aiguilles fournies avec votre cartouche, en retirant les capuchons interne et externe.",
    },
    warnings: [MISSED_DOSE],
  },
  {
    // A `verify` and a confirmation: the number in the window is the only place
    // the dose becomes visible, and her clinic changes it mid-course.
    id: "puregon.pen.set-the-dose",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Set your dose - Dial your prescribed dose and confirm the correct number appears in the dose window.",
      fr: "Régler la dose - Sélectionnez la dose prescrite et vérifiez le chiffre affiché.",
    },
  },
  {
    // The EMA requires air removed before injection and defers the method to
    // the pen's instructions-for-use; those say to tap, and say plainly that a
    // small amount of air does not change the dose. Both halves are here,
    // because the second is what stops her chasing the last bubble out of a
    // cartridge and wasting medicine doing it.
    id: "puregon.pen.remove-air-bubbles",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Remove air bubbles - Hold the pen with the needle pointing up and tap the cartridge holder gently so any air bubbles rise to the top. A small amount of air will not affect the dose you receive.",
      fr: "Chasser les bulles d'air - Tenez le stylo aiguille vers le haut et tapotez doucement le porte-cartouche pour faire remonter les bulles. Une petite quantité d'air n'affecte pas la dose reçue.",
    },
  },
  {
    id: "puregon.pen.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Clean the injection site - Swab the lower abdomen, at least 2 inches from the navel, with an alcohol swab and let dry. Alternate sides each day.",
      fr: "Désinfecter le site - Désinfectez le bas du ventre, à au moins 5 cm du nombril, à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
    },
  },
  {
    id: "puregon.pen.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Pinch the skin firmly, insert the needle at 90°, and press the injection button steadily. Listen for the click that confirms the mechanism has engaged. Keep the button depressed and hold the needle in place for 5 seconds to deliver the full dose.",
      fr: "Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et appuyez sur le bouton d'injection de façon régulière. Écoutez le clic qui confirme l'enclenchement. Maintenez le bouton enfoncé et l'aiguille en place pendant 5 secondes pour délivrer la dose complète.",
    },
    warnings: [WHEN_TO_CALL, BREATHING_OR_ALLERGIC_REACTION],
  },
  {
    // The pen is not thrown away, which is the one sentence a patient moving
    // from any other pen in this catalogue needs. The needle is, immediately.
    id: "puregon.pen.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Withdraw the needle and apply gentle pressure with gauze - do not rub. Unscrew the needle and put it straight into a sharps container - used needles are discarded immediately after injection, never left on the pen. Keep the pen itself: it is reusable. Throw the cartridge away 28 days after its first use, even if some solution is left in it. Track your injection site to rotate properly each day.",
      fr: "Après l'injection - Retirez l'aiguille et appliquez une légère pression avec une compresse - sans frotter. Dévissez l'aiguille et jetez-la aussitôt dans un collecteur - les aiguilles usagées se jettent immédiatement après l'injection et ne sont jamais laissées sur le stylo. Gardez le stylo : il est réutilisable. Jetez la cartouche 28 jours après sa première utilisation, même s'il y reste de la solution. Notez le site utilisé pour bien alterner chaque jour.",
    },
  },
];

export const PUREGON_PROTOCOL: InjectionProtocol = {
  medicationSlug: "puregon",
  brands: BRANDS,
  aliases: ["Follitropin beta", "Follitropine beta", "Follitropine bêta"],
  // Its instructions-for-use names both, so the walk asks before she cleans
  // anything and the camera then does whatever it honestly can at the site she
  // named — the same shape ganirelix and Bemfola have.
  sites: [
    {
      id: "abdomen",
      label: { en: "Lower abdomen", fr: "Bas du ventre" },
      copy: {
        en: "Clean the injection site - Swab the lower abdomen, at least 2 inches from the navel, with an alcohol swab and let dry. Alternate sides each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre, à au moins 5 cm du nombril, à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
      },
    },
    {
      id: "thigh",
      label: {
        en: "Upper outer thigh",
        fr: "Face externe du haut de la cuisse",
      },
      copy: {
        en: "If you use your thigh instead - Swab the upper outer area of your thigh with an alcohol swab and let dry. Alternate sides each day.",
        fr: "Si vous utilisez la cuisse - Désinfectez la face externe du haut de la cuisse à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
      },
    },
  ],
  indication: "stimulation",
  // In IU, as the approved content doses it ("75-450 IU daily"), and titrated:
  // her clinic sets the number and changes it mid-course, so there are no
  // strengths to check it against. Only the unit can be wrong on a record.
  dose: { unit: "IU" },
  form: "pen",
  // The cartridge pen has no value of its own in her calendar's vocabulary, and
  // "Stylo injecteur" is the button she pressed when she added it. Imprecise —
  // this pen is not prefilled — but it is her word for it, and inventing a
  // sixth option would put a device name on screen that her own form cannot
  // write. See TECHNIQUE.md.
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-09-28.1",
  derivedFrom: "content/data/medications/gonadotropins.json#puregon",
  steps: STEPS,
};
